import * as XLSX from 'xlsx';
import { parseArgentineNumber, parseFechaHoraArgentina, parseCuitDigits } from './formatters';

/**
 * Parsers de los archivos de operaciones que el trader baja del sistema con el
 * que opera, para no tener que retipear cada caución en la planilla.
 *
 * Hay un archivo por mercado y son formatos distintos:
 *
 * - BYMA: una fila por partida ejecutada, con `Cuenta` = nro de comitente, y
 *   `Orden` agrupando las partidas de una misma orden. Formato numérico
 *   argentino ("102.946.702,000") y fechas "DD/MM/AAAA HH:mm:ss".
 *
 * - A3: formato de clearing. Cada caución genera CUATRO filas (dos patas × dos
 *   registraciones): las que van contra CONTRAPARTE CENTRAL son el espejo de
 *   mercado, y las que tienen AXON CAPITAL en las dos puntas son las del
 *   cliente. Identifica al cliente por CUIT, no por comitente. Además el
 *   archivo viene scopeado por fecha de LIQUIDACIÓN, así que trae también las
 *   patas de vencimiento de cauciones concertadas días anteriores.
 *
 * - Listado de órdenes ("Captura de Ordenes" del sistema de órdenes): desde el
 *   22/10/2026 es el archivo que suben los traders. Una fila por ORDEN, de todos
 *   los productos (bonos, acciones, cauciones) y de cualquier estado, con el
 *   mercado en una columna. Lo ejecutado está en "C. Ejec." y "T. Ejec", que
 *   vienen como número de máquina ("34787.0", "0.01", "4.1107779E7"), y la tasa
 *   viene como fracción (0.2 = 20 %).
 *
 * Los tres parsers devuelven la misma forma de "partida ejecutada" para que el
 * matcher y la UI sean agnósticos del mercado.
 */

export const MERCADO_BYMA = 'BYMA';
export const MERCADO_A3 = 'A3';

/** Formato del listado de órdenes: el mercado no es del archivo sino de cada fila. */
export const FORMATO_ORDENES = 'ORDENES';

export const MONEDA_PESOS = 'Pesos';
export const MONEDA_USD = 'USD Mep';
export const MONEDA_USD_CABLE = 'USD Cable';

export const OPERACION_TOMADORA = 'Tomadora';
export const OPERACION_COLOCADORA = 'Colocadora';

const RAZON_SOCIAL_AXON = 'AXON CAPITAL';

// Base de cálculo de intereses de las cauciones (días corridos sobre 365).
const BASE_ANUAL = 365;

/** Estados que indican que la operación no llegó a existir. Es una lista negra
 *  a propósito: un estado desconocido se importa y se ve, en vez de perderse. */
const ESTADOS_ANULADOS = /(anul|cancel|rechaz|baja|error)/i;

/** En el listado de órdenes "Cancelada" no entra: una orden cancelada después de
 *  ejecutarse en parte conserva lo que ejecutó, y eso es una caución real. */
const ESTADOS_ORDEN_ANULADA = /(anul|rechaz|baja|error)/i;

// Marcas diacríticas combinantes: se arma con escapes ASCII a propósito, para
// que el patrón no dependa de cómo guarde este archivo el editor.
const DIACRITICOS = new RegExp('[\\u0300-\\u036f]', 'g');

/**
 * Normaliza un encabezado para poder buscarlo sin pelear con acentos, puntos ni
 * mayúsculas: "Fecha Concertación" -> "fecha concertacion", "F.O." -> "fo".
 */
export const normalizarHeader = (valor) =>
  String(valor ?? '')
    .normalize('NFD')
    .replace(DIACRITICOS, '')
    .replace(/[.:]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

const texto = (valor) => String(valor ?? '').trim();

// ---------------------------------------------------------------------------
// Lectura del archivo a matriz de celdas
// ---------------------------------------------------------------------------

/**
 * Decodifica bytes a texto probando UTF-8 y cayendo a windows-1252.
 *
 * El export de BYMA viene en windows-1252: "Operación" trae el byte 0xF3, que
 * es UTF-8 inválido. Decodificándolo como UTF-8 el encabezado queda roto, no se
 * encuentra la columna y se descartan todas las filas del archivo.
 */
export const decodificarTexto = (bytes) => {
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch (e) {
    try {
      return new TextDecoder('windows-1252').decode(bytes);
    } catch (e2) {
      // Último recurso sin depender de ICU: latin1 byte a byte.
      let resultado = '';
      for (let i = 0; i < bytes.length; i += 1) resultado += String.fromCharCode(bytes[i]);
      return resultado;
    }
  }
};

/** Parte una línea de CSV respetando comillas y comillas escapadas (""). */
export const dividirLineaCsv = (linea, delimitador) => {
  const campos = [];
  let actual = '';
  let entreComillas = false;

  for (let i = 0; i < linea.length; i += 1) {
    const caracter = linea[i];
    if (caracter === '"') {
      if (entreComillas && linea[i + 1] === '"') {
        actual += '"';
        i += 1;
      } else {
        entreComillas = !entreComillas;
      }
      continue;
    }
    if (caracter === delimitador && !entreComillas) {
      campos.push(actual.trim());
      actual = '';
      continue;
    }
    actual += caracter;
  }
  campos.push(actual.trim());
  return campos;
};

/** El delimitador es el que parte el encabezado en más columnas. */
const detectarDelimitador = (linea) =>
  [',', ';', '\t'].reduce(
    (mejor, candidato) =>
      dividirLineaCsv(linea, candidato).length > dividirLineaCsv(linea, mejor).length ? candidato : mejor,
    ','
  );

export const csvAMatriz = (contenido) => {
  const limpio = contenido.charCodeAt(0) === 0xfeff ? contenido.slice(1) : contenido;
  const lineas = limpio.split(/\r?\n/).filter((l) => l.trim() !== '');
  if (lineas.length === 0) return [];
  const delimitador = detectarDelimitador(lineas[0]);
  return lineas.map((linea) => dividirLineaCsv(linea, delimitador));
};

/**
 * Convierte el contenido crudo de un archivo en la matriz de celdas.
 *
 * El formato se detecta por los magic bytes y no por la extensión, porque el
 * export de A3 se llama ".xls" pero en realidad es un xlsx (un zip OOXML).
 *
 * @param {Uint8Array} bytes
 * @returns {Array<Array<string|number|Date>>}
 */
export const bytesAMatriz = (bytes) => {
  const esZip = bytes[0] === 0x50 && bytes[1] === 0x4b; // "PK" -> xlsx/xlsm
  const esXlsLegacy = bytes[0] === 0xd0 && bytes[1] === 0xcf; // documento OLE2

  if (esZip || esXlsLegacy) {
    const workbook = XLSX.read(bytes, { type: 'array', cellDates: true });
    const hoja = workbook.Sheets[workbook.SheetNames[0]];
    if (!hoja) return [];
    return XLSX.utils.sheet_to_json(hoja, { header: 1, raw: true, defval: '', blankrows: false });
  }

  return csvAMatriz(decodificarTexto(bytes));
};

const esAxon = (valor) =>
  normalizarHeader(valor).toUpperCase().includes(RAZON_SOCIAL_AXON);

/*
 * El cable se distingue del MEP por la marca de plaza en la especie ("USDC",
 * "USD-C", "DOLAR CABLE", "CCL"). Se chequea antes que el dólar genérico porque
 * toda especie de cable matchea también el patrón de USD.
 */
const ES_CABLE = /\bCABLE\b|\bCCL\b|\bUSD\s*-?\s*C\b|\bUSDC\b/;

const monedaDesdeEspecie = (especie, monedaFallback) => {
  const valor = `${texto(especie)} ${texto(monedaFallback)}`.toUpperCase();
  if (ES_CABLE.test(valor)) return MONEDA_USD_CABLE;
  if (/USD|DOLAR|DÓLAR/.test(valor)) return MONEDA_USD;
  if (/ARS|PESO/.test(valor)) return MONEDA_PESOS;
  return '';
};

/**
 * La especie que se opera es la moneda misma: comprar pesos es recibirlos, o
 * sea tomar prestado. De ahí que Compra = Tomadora, tanto en el export de BYMA
 * como en el listado de órdenes (confirmado por la mesa el 22/10/2026).
 */
const operacionDesdeLado = (lado) => {
  const valor = texto(lado).toLowerCase();
  if (valor.startsWith('compra')) return OPERACION_TOMADORA;
  if (valor.startsWith('venta')) return OPERACION_COLOCADORA;
  return '';
};

/**
 * Lee las columnas que el sistema de órdenes escribe como número de máquina
 * ("34787.0", "0.01", "4.1107779E7"). No pueden pasar por parseArgentineNumber:
 * ahí "25220.691" se leería como 25.220.691 y la notación científica, que es
 * como vienen los montos desde 10 millones, perdería la "E" y el exponente.
 */
const numeroDeMaquina = (valor) => {
  if (typeof valor === 'number') return Number.isFinite(valor) ? valor : 0;
  const limpio = texto(valor);
  if (!limpio) return 0;
  const numero = Number(limpio);
  return Number.isFinite(numero) ? numero : 0;
};

const mercadoDesdeColumna = (valor) => {
  const mercado = texto(valor).toUpperCase();
  if (mercado.includes('BYMA')) return MERCADO_BYMA;
  if (/A3|MATBA|ROFEX|MAE/.test(mercado)) return MERCADO_A3;
  return '';
};

/**
 * El vencimiento que trae la especie de caución del sistema de órdenes, en
 * formato DDMM: "PESOS-2310-U-CT-ARS" vence el 23/10. El año es el de la
 * operación, o el siguiente si la caución cruza el fin de año.
 */
const vencimientoDesdeEspecie = (especie, fechaOperacion) => {
  const match = texto(especie).match(/-(\d{2})(\d{2})-/);
  if (!match || !fechaOperacion) return null;
  const [, dia, mes] = match.map(Number);
  let vencimiento = new Date(fechaOperacion.getFullYear(), mes - 1, dia);
  if (vencimiento.getMonth() !== mes - 1) return null;
  if (vencimiento < fechaOperacion) {
    vencimiento = new Date(fechaOperacion.getFullYear() + 1, mes - 1, dia);
  }
  return vencimiento;
};

/** Días corridos entre dos fechas, ignorando la hora. */
const diasEntre = (desde, hasta) => {
  if (!desde || !hasta) return 0;
  const a = new Date(desde.getFullYear(), desde.getMonth(), desde.getDate());
  const b = new Date(hasta.getFullYear(), hasta.getMonth(), hasta.getDate());
  return Math.round((b - a) / 86400000);
};

/** Clave de día local, para comparar fechas de rueda sin líos de zona horaria. */
export const claveDia = (fecha) => {
  if (!fecha) return '';
  const mes = String(fecha.getMonth() + 1).padStart(2, '0');
  const dia = String(fecha.getDate()).padStart(2, '0');
  return `${fecha.getFullYear()}-${mes}-${dia}`;
};

/**
 * Verifica que el monto de vencimiento cierre con capital, tasa y plazo. Es la
 * red contra un cambio de columnas en el export: si el archivo cambia y
 * empezamos a leer la columna equivocada, esto lo detecta.
 * Se aceptan las dos bases usuales (365 y 360) antes de dar por sospechosa la fila.
 */
const controlarInteres = ({ capital, tasa, dias, montoVencimiento }) => {
  if (!montoVencimiento || !capital || !dias) return null;
  const toleranciaAbs = Math.max(0.05, capital * 1e-6);
  const cierraCon = (base) =>
    Math.abs(capital * (1 + (tasa / 100) * (dias / base)) - montoVencimiento) <= toleranciaAbs;
  if (cierraCon(BASE_ANUAL) || cierraCon(360)) return null;
  const esperado = capital * (1 + (tasa / 100) * (dias / BASE_ANUAL));
  return `El monto de vencimiento (${montoVencimiento}) no cierra con capital ${capital} a ${tasa}% por ${dias} día(s): se esperaba ${esperado.toFixed(2)}`;
};

/**
 * Detecta qué archivo es mirando los encabezados.
 * @param {string[]} headers - encabezados crudos de la fila de títulos
 * @returns {'ORDENES'|'BYMA'|'A3'|null}
 */
export const detectarFormato = (headers) => {
  const set = new Set((headers || []).map(normalizarHeader));
  if (set.has('id') && set.has('c/v') && set.has('producto') && set.has('c ejec')) return FORMATO_ORDENES;
  if (set.has('secuencia referencia') && set.has('numero pata')) return MERCADO_A3;
  if (set.has('fo') && set.has('cuenta') && set.has('lado')) return MERCADO_BYMA;
  return null;
};

/**
 * Encuentra la fila de encabezados y devuelve las filas de datos como objetos
 * indexados por header normalizado.
 *
 * Busca el header en las primeras filas en vez de asumir la primera, porque el
 * archivo de A3 arranca en la columna B y puede traer filas de título arriba.
 *
 * @param {Array<Array<string|number>>} matriz - filas crudas (array de arrays)
 * @returns {{ formato: string|null, headers: string[], filas: object[], filaHeader: number }}
 */
export const matrizAObjetos = (matriz) => {
  const filasAInspeccionar = Math.min(10, (matriz || []).length);
  for (let i = 0; i < filasAInspeccionar; i++) {
    const posiblesHeaders = (matriz[i] || []).map(texto);
    const formato = detectarFormato(posiblesHeaders);
    if (!formato) continue;

    const headers = posiblesHeaders.map(normalizarHeader);
    const filas = [];
    for (let j = i + 1; j < matriz.length; j++) {
      const cruda = matriz[j] || [];
      // Filas totalmente vacías (separadores, totales al pie) se descartan.
      if (cruda.every((celda) => texto(celda) === '')) continue;
      const objeto = { __fila: j + 1 };
      headers.forEach((header, col) => {
        if (header) objeto[header] = cruda[col];
      });
      filas.push(objeto);
    }
    return { formato, headers, filas, filaHeader: i + 1 };
  }
  return { formato: null, headers: [], filas: [], filaHeader: 0 };
};

/** La fecha de rueda del archivo: la concertación más reciente que trae. */
const detectarFechaRueda = (fechas) => {
  const validas = fechas.filter(Boolean);
  if (!validas.length) return null;
  return validas.reduce((max, f) => (f > max ? f : max), validas[0]);
};

/**
 * `cantidadOrden` es lo que pidió la orden. En los export de BYMA y A3 cada
 * fila es una ejecución terminada, así que coincide con el capital; en el
 * listado de órdenes puede ser mayor mientras la orden se sigue ejecutando.
 */
const partidaBase = ({
  mercado, idExterno, ordenExterna, nroComitente, cuit, operacion, moneda,
  capital, cantidadOrden, tasa, dias, montoVencimiento, fechaConcertacion, esCarteraPropia, filas,
}) => ({
  mercado,
  idExterno,
  ordenExterna,
  nroComitente: nroComitente || '',
  cuit: cuit || '',
  operacion,
  moneda,
  capital,
  cantidadOrden: Math.max(cantidadOrden || 0, capital),
  tasa,
  dias,
  montoVencimiento: montoVencimiento || 0,
  fechaConcertacion,
  esCarteraPropia: Boolean(esCarteraPropia),
  filasOrigen: filas,
});

/**
 * Parsea las filas del archivo de BYMA. Cada fila es una partida.
 *
 * @param {object[]} filas - salida de matrizAObjetos
 * @param {{ fechaRueda?: Date }} opciones - si se pasa, descarta lo concertado otro día
 * @returns {{ partidas: object[], ignoradas: object[], advertencias: object[], fechaRueda: Date|null }}
 */
export const parseBymaRows = (filas, { fechaRueda } = {}) => {
  const partidas = [];
  const ignoradas = [];
  const advertencias = [];

  const candidatas = [];
  (filas || []).forEach((fila) => {
    const ignorar = (motivo) => ignoradas.push({ fila: fila.__fila, motivo });

    if (!/cauc/i.test(texto(fila['fo']))) {
      ignorar(`No es una caución (F.O. = "${texto(fila['fo']) || 'vacío'}")`);
      return;
    }
    const estado = texto(fila['estado']);
    if (estado && ESTADOS_ANULADOS.test(estado)) {
      ignorar(`Operación anulada o rechazada (Estado = "${estado}")`);
      return;
    }

    const nroComitente = texto(fila['cuenta']).replace(/\D/g, '');
    if (!nroComitente) {
      ignorar('La fila no trae número de cuenta');
      return;
    }

    const operacion = operacionDesdeLado(fila['lado']);
    if (!operacion) {
      ignorar(`No se pudo determinar si es tomadora o colocadora (Lado = "${texto(fila['lado']) || 'vacío'}")`);
      return;
    }

    const moneda = monedaDesdeEspecie(fila['especie'], fila['moneda']);
    if (!moneda) {
      ignorar(`Moneda no reconocida (Especie = "${texto(fila['especie']) || 'vacío'}")`);
      return;
    }

    const capital = parseArgentineNumber(fila['cantidad']);
    if (!(capital > 0)) {
      ignorar(`Capital inválido (Cantidad = "${texto(fila['cantidad']) || 'vacío'}")`);
      return;
    }

    const fechaConcertacion = parseFechaHoraArgentina(fila['hora']);
    if (!fechaConcertacion) {
      ignorar(`Fecha de concertación ilegible (Hora = "${texto(fila['hora']) || 'vacío'}")`);
      return;
    }

    const idExterno = texto(fila['operacion']);
    if (!idExterno) {
      ignorar('La fila no trae número de operación, que es lo que evita importarla dos veces');
      return;
    }

    const fechaVencimiento = parseFechaHoraArgentina(fila['vto']);
    candidatas.push({
      fila,
      partida: partidaBase({
        mercado: MERCADO_BYMA,
        idExterno,
        ordenExterna: texto(fila['orden']) || idExterno,
        nroComitente,
        cuit: '',
        operacion,
        moneda,
        capital,
        tasa: parseArgentineNumber(fila['precio']),
        dias: diasEntre(fechaConcertacion, fechaVencimiento),
        montoVencimiento: parseArgentineNumber(fila['monto']),
        fechaConcertacion,
        filas: [fila.__fila],
      }),
    });
  });

  const fechaDetectada =
    fechaRueda || detectarFechaRueda(candidatas.map((c) => c.partida.fechaConcertacion));
  const claveRueda = claveDia(fechaDetectada);

  candidatas.forEach(({ fila, partida }) => {
    if (claveRueda && claveDia(partida.fechaConcertacion) !== claveRueda) {
      ignoradas.push({
        fila: fila.__fila,
        motivo: `Concertada el ${claveDia(partida.fechaConcertacion)}, no en la rueda del ${claveRueda}`,
      });
      return;
    }
    const aviso = controlarInteres(partida);
    if (aviso) advertencias.push({ fila: fila.__fila, idExterno: partida.idExterno, motivo: aviso });
    partidas.push(partida);
  });

  return { partidas, ignoradas, advertencias, fechaRueda: fechaDetectada };
};

/**
 * Cantidad de la fila, normalizada para usarla como parte de una clave.
 *
 * Las dos patas de una misma partida comparten Cantidad (lo que cambia entre
 * ellas es el Monto, que en la de Futuro trae los intereses), así que sirve para
 * separar partidas sin desarmar sus patas. Si el archivo no la trae, todas las
 * filas caen en la misma clave y se resuelve como antes.
 */
const claveCantidad = (fila) => {
  const cantidad = parseArgentineNumber(fila['cantidad']);
  return cantidad > 0 ? String(cantidad) : '';
};

/**
 * Parsea las filas del archivo de A3.
 *
 * Se agrupan las filas por Secuencia Referencia (que es la misma en las cuatro
 * patas de una caución), por Cantidad y por CUIT del cliente. De cada grupo se
 * usa la pata Contado (T0, el capital sin intereses) para el capital y la
 * dirección, y la pata Futuro (T1) para el plazo y el monto de vencimiento.
 *
 * La Cantidad entra en la clave porque la Secuencia Referencia sola no alcanza:
 * son 17 dígitos guardados como número, más de los que entran en un float de
 * doble precisión, así que el archivo ya llega con los dos últimos redondeados a
 * cero. Dos partidas de la misma orden ejecutadas en el mismo segundo terminan
 * compartiendo secuencia, y sin desempatarlas el grupo quedaba con dos patas
 * Contado y se descartaba entero: la caución se conciliaba de menos (o no se
 * conciliaba) y el trader la veía quedar "En Proceso".
 *
 * @param {object[]} filas - salida de matrizAObjetos
 * @param {{ fechaRueda?: Date }} opciones
 * @returns {{ partidas: object[], ignoradas: object[], advertencias: object[], fechaRueda: Date|null }}
 */
export const parseA3Rows = (filas, { fechaRueda } = {}) => {
  const ignoradas = [];
  const advertencias = [];

  // Paso 1: quedarse con las cauciones vivas y clasificar cada fila.
  const grupos = new Map();
  (filas || []).forEach((fila) => {
    const ignorar = (motivo) => ignoradas.push({ fila: fila.__fila, motivo });

    const negociacion = texto(fila['negociacion']);
    if (!/cauc/i.test(negociacion)) {
      ignorar(`No es una caución (Negociación = "${negociacion || 'vacío'}")`);
      return;
    }
    const estado = texto(fila['estado']);
    if (estado && ESTADOS_ANULADOS.test(estado)) {
      ignorar(`Operación anulada o rechazada (Estado = "${estado}")`);
      return;
    }

    const seq = texto(fila['secuencia referencia']);
    if (!seq) {
      ignorar('La fila no trae Secuencia Referencia, que es lo que agrupa las patas');
      return;
    }

    const compradorEsAxon = esAxon(fila['comprador']);
    const vendedorEsAxon = esAxon(fila['vendedor']);
    const cuitComprador = parseCuitDigits(fila['cuit comprador']);
    const cuitVendedor = parseCuitDigits(fila['cuit vendedor']);

    // Las filas del cliente son las que tienen AXON en las dos puntas; las que
    // van contra la contraparte central son su espejo de mercado. Sólo si una
    // secuencia no tiene ninguna fila de cliente se usa el espejo, y ahí puede
    // ser cartera propia de Axon (sin CUIT) o un cliente cuya fila interna no
    // vino en el archivo (con CUIT), así que el CUIT se lee en los dos casos.
    const esFilaCliente = compradorEsAxon && vendedorEsAxon;
    const cuit = cuitComprador || cuitVendedor;
    const cantidad = claveCantidad(fila);
    // La partida, no la secuencia, es lo que hay que identificar: dos partidas
    // pueden compartir secuencia por el redondeo del archivo.
    const claveOperacion = `${seq}|${cantidad}`;
    const clave = `${claveOperacion}|${cuit}|${esFilaCliente ? 'cli' : 'ccp'}`;

    if (!grupos.has(clave)) {
      grupos.set(clave, { seq, cantidad, claveOperacion, cuit, esFilaCliente, filas: [] });
    }
    grupos.get(clave).filas.push({
      fila,
      compradorEsAxon,
      vendedorEsAxon,
      cuitComprador,
      cuitVendedor,
    });
  });

  // Paso 2: por partida, priorizar las filas del cliente sobre el espejo.
  const operacionesConCliente = new Set();
  grupos.forEach((grupo) => {
    if (grupo.esFilaCliente) operacionesConCliente.add(grupo.claveOperacion);
  });

  const candidatas = [];
  grupos.forEach((grupo) => {
    const { seq, cantidad, claveOperacion, cuit, esFilaCliente, filas: filasGrupo } = grupo;
    const nrosFila = filasGrupo.map((f) => f.fila.__fila);
    const ignorarGrupo = (motivo) => nrosFila.forEach((n) => ignoradas.push({ fila: n, motivo }));

    if (!esFilaCliente && operacionesConCliente.has(claveOperacion)) {
      // Espejo contra la contraparte central de una caución de cliente ya tomada.
      ignorarGrupo(`Espejo de mercado de la secuencia ${seq} (la operación del cliente se toma de las filas de Axon)`);
      return;
    }

    const patas = (patron) =>
      filasGrupo.filter((f) => patron.test(normalizarHeader(f.fila['numero pata'])));
    let t0 = patas(/contado/)[0];
    let t1 = patas(/futuro/)[0];

    // Si el archivo dejara de traer "Número Pata", T0 es la de menor monto:
    // T1 siempre es T0 + intereses.
    if (!t0 || !t1) {
      const porMonto = [...filasGrupo].sort(
        (a, b) => parseArgentineNumber(a.fila['monto']) - parseArgentineNumber(b.fila['monto'])
      );
      if (porMonto.length === 2) {
        t0 = t0 || porMonto[0];
        t1 = t1 || porMonto[1];
      }
    }

    if (!t0) {
      ignorarGrupo(`La secuencia ${seq} sólo trae la pata de vencimiento (se concertó en otra rueda)`);
      return;
    }
    if (patas(/contado/).length > 1 || patas(/futuro/).length > 1) {
      ignorarGrupo(
        `La secuencia ${seq} trae más de una pata Contado o Futuro para el mismo cliente por la misma cantidad; revisar el archivo a mano`
      );
      return;
    }

    const capital = parseArgentineNumber(t0.fila['cantidad']) || parseArgentineNumber(t0.fila['monto']);
    if (!(capital > 0)) {
      ignorarGrupo(`Capital inválido en la secuencia ${seq}`);
      return;
    }

    const fechaConcertacion = parseFechaHoraArgentina(t0.fila['fecha concertacion']);
    if (!fechaConcertacion) {
      ignorarGrupo(`Fecha de concertación ilegible en la secuencia ${seq}`);
      return;
    }

    const moneda = monedaDesdeEspecie(t0.fila['especie'], t0.fila['moneda']);
    if (!moneda) {
      ignorarGrupo(`Moneda no reconocida en la secuencia ${seq} (Especie = "${texto(t0.fila['especie']) || 'vacío'}")`);
      return;
    }

    // En la pata Contado quien compra recibe la plata, así que es el tomador
    // (la especie que se opera es la moneda misma). Si la fila trae CUIT, el
    // lado del CUIT define la dirección; si no lo trae es cartera propia y
    // manda el lado en el que está Axon.
    const operacion = t0.cuitComprador
      ? OPERACION_TOMADORA
      : t0.cuitVendedor
        ? OPERACION_COLOCADORA
        : t0.compradorEsAxon
          ? OPERACION_TOMADORA
          : OPERACION_COLOCADORA;

    const fechaVencimiento = t1 ? parseFechaHoraArgentina(t1.fila['fecha liquidacion']) : null;

    candidatas.push({
      nrosFila,
      seq,
      cantidad,
      cuit,
      base: {
        mercado: MERCADO_A3,
        ordenExterna: seq,
        nroComitente: '',
        cuit,
        operacion,
        moneda,
        capital,
        tasa: parseArgentineNumber(t0.fila['tasa']),
        dias: diasEntre(fechaConcertacion, fechaVencimiento),
        montoVencimiento: t1 ? parseArgentineNumber(t1.fila['monto']) : 0,
        fechaConcertacion,
        esCarteraPropia: !cuit,
        filas: nrosFila,
      },
    });
  });

  // Paso 3: la clave externa, que es lo que evita importar dos veces la misma
  // partida. Normalmente alcanza la secuencia; sólo cuando el redondeo del
  // archivo la hace compartir con otra partida se le agrega la cantidad, para
  // no cambiarle el id a lo que ya se venía importando bien.
  const partidasPorSecuencia = new Map();
  candidatas.forEach(({ seq, cuit }) => {
    const clave = `${seq}|${cuit}`;
    partidasPorSecuencia.set(clave, (partidasPorSecuencia.get(clave) || 0) + 1);
  });

  const conPartida = candidatas.map(({ nrosFila, seq, cantidad, cuit, base }) => ({
    nrosFila,
    partida: partidaBase({
      ...base,
      idExterno: [seq, cuit, partidasPorSecuencia.get(`${seq}|${cuit}`) > 1 ? cantidad : '']
        .filter(Boolean)
        .join('-'),
    }),
  }));

  const fechaDetectada =
    fechaRueda || detectarFechaRueda(conPartida.map((c) => c.partida.fechaConcertacion));
  const claveRueda = claveDia(fechaDetectada);

  const partidas = [];
  conPartida.forEach(({ nrosFila, partida }) => {
    if (claveRueda && claveDia(partida.fechaConcertacion) !== claveRueda) {
      nrosFila.forEach((n) =>
        ignoradas.push({
          fila: n,
          motivo: `Concertada el ${claveDia(partida.fechaConcertacion)}, no en la rueda del ${claveRueda}`,
        })
      );
      return;
    }
    const aviso = controlarInteres(partida);
    if (aviso) advertencias.push({ fila: nrosFila[0], idExterno: partida.idExterno, motivo: aviso });
    partidas.push(partida);
  });

  return { partidas, ignoradas, advertencias, fechaRueda: fechaDetectada };
};

const fechaParaMensaje = (fecha) => fecha.toLocaleDateString('es-AR');

/**
 * Días de la caución. El vencimiento sale de la especie, que lo trae sin
 * ambigüedad; "Fecha Liquidación" queda de respaldo y de control, porque sólo
 * se la vio en cauciones a 1 día y no está probado qué trae en plazos largos.
 */
const plazoDeOrden = (fila, fechaConcertacion) => {
  const porEspecie = vencimientoDesdeEspecie(fila['especie'], fechaConcertacion);
  const porLiquidacion = parseFechaHoraArgentina(fila['fecha liquidacion']);
  const difieren =
    porEspecie && porLiquidacion && claveDia(porEspecie) !== claveDia(porLiquidacion);
  return {
    dias: diasEntre(fechaConcertacion, porEspecie || porLiquidacion),
    aviso: difieren
      ? `La especie vence el ${fechaParaMensaje(porEspecie)} pero la Fecha Liquidación dice ${fechaParaMensaje(porLiquidacion)}: se tomó la de la especie`
      : null,
  };
};

/**
 * Lee una fila del listado de órdenes: devuelve la partida, o el motivo por el
 * que no se importa.
 */
const leerOrden = (fila) => {
  const valor = (columna) => texto(fila[columna]) || 'vacío';

  if (!/cauc/i.test(texto(fila['producto']))) {
    return { motivo: `No es una caución (Producto = "${valor('producto')}")` };
  }
  const estado = texto(fila['estado']);
  if (estado && ESTADOS_ORDEN_ANULADA.test(estado)) {
    return { motivo: `Orden anulada o rechazada (Estado = "${estado}")` };
  }

  // Vale lo ejecutado, no lo pedido: una orden "Creada" todavía no operó nada y
  // una parcial vale por lo que ya ejecutó.
  const capital = numeroDeMaquina(fila['c ejec']);
  if (!(capital > 0)) {
    return { motivo: `La orden todavía no se ejecutó (Estado = "${valor('estado')}")`, sinEjecutar: true };
  }
  const cantidadOrden = parseArgentineNumber(fila['cantidad']);
  if (cantidadOrden > 0 && capital > cantidadOrden + 0.5) {
    return { motivo: `Lo ejecutado (${capital}) supera la cantidad de la orden (${cantidadOrden}); revisar el archivo a mano` };
  }

  const idExterno = texto(fila['id']);
  if (!idExterno) return { motivo: 'La fila no trae ID de orden, que es lo que evita importarla dos veces' };
  const nroComitente = texto(fila['cuenta']).replace(/\D/g, '');
  if (!nroComitente) return { motivo: 'La fila no trae número de cuenta' };
  const mercado = mercadoDesdeColumna(fila['mercado']);
  if (!mercado) return { motivo: `Mercado no reconocido (Mercado = "${valor('mercado')}")` };
  const operacion = operacionDesdeLado(fila['c/v']);
  if (!operacion) return { motivo: `No se pudo determinar si es tomadora o colocadora (C/V = "${valor('c/v')}")` };
  const moneda = monedaDesdeEspecie(fila['especie'], '');
  if (!moneda) return { motivo: `Moneda no reconocida (Especie = "${valor('especie')}")` };
  const fechaConcertacion = parseFechaHoraArgentina(fila['fecha operacion']);
  if (!fechaConcertacion) return { motivo: `Fecha de operación ilegible (Fecha Operación = "${valor('fecha operacion')}")` };

  // La tasa viene como fracción (0.01 = 1 %) y la planilla la guarda en
  // porcentaje. Se redondea para no arrastrar ruido de coma flotante.
  const tasaFraccion = numeroDeMaquina(fila['t ejec']) || numeroDeMaquina(fila['tasa']);
  const { dias, aviso } = plazoDeOrden(fila, fechaConcertacion);

  return {
    aviso,
    partida: partidaBase({
      mercado,
      idExterno,
      ordenExterna: idExterno,
      nroComitente,
      cuit: '',
      operacion,
      moneda,
      capital,
      cantidadOrden,
      tasa: Math.round(tasaFraccion * 100 * 1e6) / 1e6,
      dias,
      montoVencimiento: numeroDeMaquina(fila['monto ejecutado']),
      fechaConcertacion,
      filas: [fila.__fila],
    }),
  };
};

/**
 * Parsea las filas del listado de órdenes. Cada orden de caución con algo
 * ejecutado es una partida, y su ID es la clave externa: al resubir el archivo
 * a medida que se ejecutan las órdenes, la misma partida se actualiza en vez de
 * duplicarse.
 *
 * @param {object[]} filas - salida de matrizAObjetos
 * @param {{ fechaRueda?: Date }} opciones - si se pasa, descarta lo operado otro día
 * @returns {{ partidas: object[], ignoradas: object[], advertencias: object[], fechaRueda: Date|null }}
 */
export const parseOrdenesRows = (filas, { fechaRueda } = {}) => {
  const ignoradas = [];
  const advertencias = [];
  const candidatas = [];
  const idsVistos = new Set();

  (filas || []).forEach((fila) => {
    const { partida, motivo, sinEjecutar, aviso } = leerOrden(fila);
    if (!partida) {
      ignoradas.push({ fila: fila.__fila, motivo, ...(sinEjecutar ? { sinEjecutar } : {}) });
      return;
    }
    if (idsVistos.has(partida.idExterno)) {
      ignoradas.push({ fila: fila.__fila, motivo: `La orden ${partida.idExterno} aparece repetida en el archivo; se tomó la primera` });
      return;
    }
    idsVistos.add(partida.idExterno);
    candidatas.push({ fila, partida, aviso });
  });

  const fechaDetectada =
    fechaRueda || detectarFechaRueda(candidatas.map((c) => c.partida.fechaConcertacion));
  const claveRueda = claveDia(fechaDetectada);

  const partidas = [];
  candidatas.forEach(({ fila, partida, aviso }) => {
    if (claveRueda && claveDia(partida.fechaConcertacion) !== claveRueda) {
      ignoradas.push({
        fila: fila.__fila,
        motivo: `Concertada el ${claveDia(partida.fechaConcertacion)}, no en la rueda del ${claveRueda}`,
      });
      return;
    }
    [aviso, controlarInteres(partida)].filter(Boolean).forEach((motivo) =>
      advertencias.push({ fila: fila.__fila, idExterno: partida.idExterno, motivo })
    );
    partidas.push(partida);
  });

  return { partidas, ignoradas, advertencias, fechaRueda: fechaDetectada };
};

/**
 * Punto de entrada: recibe la matriz cruda del archivo y devuelve las partidas
 * normalizadas, sin importar de qué archivo ni de qué mercado sea.
 *
 * `mercado` es para mostrar: en el listado de órdenes cada partida trae el suyo.
 *
 * @param {Array<Array<string|number>>} matriz
 * @param {{ fechaRueda?: Date, formato?: string }} opciones
 */
export const parsearArchivoCauciones = (matriz, { fechaRueda, formato } = {}) => {
  const { formato: detectado, filas, filaHeader } = matrizAObjetos(matriz);
  const tipo = formato || detectado;

  if (!tipo) {
    return {
      mercado: null,
      partidas: [],
      ignoradas: [],
      advertencias: [],
      fechaRueda: null,
      error:
        'No se reconocieron las columnas del archivo. Se espera el listado de órdenes (con ID, C/V, Producto y C. Ejec.), o los export anteriores de BYMA (con Cuenta, Lado y F.O.) o de A3 (con Secuencia Referencia y Número Pata).',
    };
  }

  if (tipo === FORMATO_ORDENES) {
    const resultado = parseOrdenesRows(filas, { fechaRueda });
    const mercados = [...new Set(resultado.partidas.map((p) => p.mercado))];
    return { mercado: mercados.join(' y ') || 'listado de órdenes', filaHeader, ...resultado, error: null };
  }

  const resultado =
    tipo === MERCADO_A3
      ? parseA3Rows(filas, { fechaRueda })
      : parseBymaRows(filas, { fechaRueda });

  return { mercado: tipo, filaHeader, ...resultado, error: null };
};
